import { AxiosResponse } from "axios";
import { Api } from "@/core/services/api.service";
import * as Sentry from "@sentry/vue";
import { BuybackWebCreate } from "@/apps/whiteLabelWebsite/buyback/core/domain/service/DTO/buyback/buybackWebCreate";
import { BuybackCancellationDetails } from "@/apps/shared/buyback/core/domain/model/cancellation/BuybackCancellationDetails";
import { Buyback } from "@/apps/shared/buyback/core/domain/model/Buyback";
import {
  BuybackFinalizeRequest,
  BuybackStoreCreateRequest,
} from "@/apps/shared/buyback/core/infrastructure/mapper/buyback/buybackMapper";
import {
  ShippingLabelNotAvailable,
  ShippingLabelNotAvailableYet,
  ShippingLabelNotExpected,
} from "@/apps/dashboard/buyback/core/domain/error";
import { RawBuybackInstructions } from "@/apps/shared/buyback/core/infrastructure/repository/type/instructions/RawBuybackInstructions";
import { RawBuybackAdditionalInfos } from "@/apps/shared/buyback/core/infrastructure/repository/type/RawBuybackAdditionalInfos";
import { RawBuybackSettings } from "@/apps/shared/buyback/core/infrastructure/repository/type/RawBuybackSettings";

class BuybackClient {
  private POLLING_INTERVALS = [3000, 5000, 10000, 15000];

  /**
   * @throws {Error} Throws an error if promise rejects.
   */
  async getBuybackById(buybackId: string): Promise<{}> {
    try {
      const response = await Api.new().get(`/buybacks/${buybackId}`, {
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });

      if (response.status !== 200) {
        throw new Error(`Failed to get buyback by id. Status: ${response.status}`);
      }
      return response.data;
    } catch {
      throw new Error("Error while fetching");
    }
  }

  /**
   * @throws {Error} Throws an error if promise rejects.
   */
  async getBuybackByIdentificationNumber(buybackIdentificationNumber: string): Promise<{}> {
    try {
      const response = await Api.new().get(`/buybacks/by-identification-number/${buybackIdentificationNumber}`, {
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });

      if (response.status !== 200) {
        throw new Error(`Failed to get buyback by buybackIdentificationNumber. Status: ${response.status}`);
      }
      return response.data;
    } catch {
      throw new Error("Error while fetching");
    }
  }

  async finalizeCart(buybackWebCreateDtos: BuybackWebCreate[]): Promise<{ cart_id: string }> {
    const response = await Api.new().post("/carts", { buybacks: buybackWebCreateDtos });

    return response.data;
  }

  /**
   * @throws {Error} Throws an error if promise rejects.
   */
  async postStoreBuyback(payload: BuybackStoreCreateRequest, storeId: string): Promise<{ id: string }> {
    try {
      const response = await Api.new().post(`/store/${storeId}/buybacks`, payload);

      return response.data;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  /**
   * @throws {Error} Throws an error if promise rejects.
   */
  async postFinalizeWebToStoreBuyback(
    payload: BuybackFinalizeRequest,
    storeId: string,
    buybackId: string
  ): Promise<{ id: string }> {
    try {
      const response = await Api.new().post(`/store/${storeId}/buybacks/${buybackId}/finalize`, payload);

      return response.data;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  /**
   * @throws {Error} Throws an error if promise rejects.
   */
  async getBuybackInstructions(buybackId: string): Promise<RawBuybackInstructions> {
    try {
      const response: AxiosResponse<RawBuybackInstructions> = await Api.new().get(
        `/buybacks/${buybackId}/instructions`,
        {
          headers: { Accept: "application/json", "Content-Type": "application/json" },
        }
      );

      if (response.status !== 200) {
        throw new Error(`Failed to get buyback instructions. Status: ${response.status}`);
      }

      return response.data;
    } catch {
      throw new Error("Error while fetching");
    }
  }

  /**
   * @throws {Error} Throws an error if promise rejects.
   */
  async getBuybackAdditionalInfos(buybackId: string): Promise<RawBuybackAdditionalInfos> {
    try {
      const response: AxiosResponse<RawBuybackAdditionalInfos> = await Api.new().get(
        `/buybacks/${buybackId}/additional-infos`,
        {
          headers: { Accept: "application/json", "Content-Type": "application/json" },
        }
      );

      if (response.status !== 200) {
        throw new Error(`Failed to get buyback additional infos. Status: ${response.status}`);
      }

      return response.data;
    } catch {
      throw new Error("Error while fetching");
    }
  }

  /**
   * @throws {Error} Throws an error if promise rejects.
   */
  async postCancelBuyback(buybackId: string, buybackCancellationDetails: BuybackCancellationDetails): Promise<Buyback> {
    try {
      const response = await Api.new().post(`/buybacks/${buybackId}/cancel`, buybackCancellationDetails);

      return response.data;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  private async headBuybackShippingLabel(buybackId: string): Promise<number> {
    try {
      const response: AxiosResponse<void> = await Api.new().head(`/buybacks/${buybackId}/shipping-label`);
      return response.status;
    } catch (error: any) {
      if (error.response?.status === 425) {
        throw new ShippingLabelNotAvailableYet();
      }

      if (error.response?.status === 404) {
        throw new ShippingLabelNotExpected();
      }

      throw error;
    }
  }

  async isShippingLabelReady(buybackId: string): Promise<boolean> {
    for (const pollingInterval of this.POLLING_INTERVALS) {
      await new Promise<void>((resolve) => setTimeout(() => resolve(), pollingInterval));

      try {
        await this.headBuybackShippingLabel(buybackId);
        return true;
      } catch (error) {
        if (error instanceof ShippingLabelNotAvailableYet) {
          if (pollingInterval === this.POLLING_INTERVALS[this.POLLING_INTERVALS.length - 1]) {
            throw new ShippingLabelNotAvailable();
          }
          continue;
        }
        throw error;
      }
    }

    return true;
  }

  /**
   * @throws {Error} Throws an error if promise rejects.
   */
  async getBuybackSettings(): Promise<RawBuybackSettings> {
    try {
      const response: AxiosResponse<RawBuybackSettings> = await Api.new().get(`/buyback-settings`, {
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });

      if (response.status !== 200) {
        throw new Error(`Failed to get buyback settings. Status: ${response.status}`);
      }

      return response.data;
    } catch {
      throw new Error("Error while fetching");
    }
  }
}

export default new BuybackClient();
